<template>
  <div>
    <div class="chart-filters-button d-flex justify-content-center align-items-center"
         :class="{'show': chart.showFilters}" @click="toggleFilters()">
      <font-awesome-icon :icon="chart.showFilters ? 'chevron-right' : 'chevron-left'"></font-awesome-icon>
    </div>
    <div class="chart-filters-tab py-3 d-flex flex-column align-items-center justify-content-between"
         :class="{'show': chart.showFilters}">
      <div>
        <div class="chart-filters-title mb-3">Data breakdown:</div>
        <div class="input-wrapper">
          <label>Metadata Type:</label>
          <el-select
              placeholder="Select values"
              v-model="chart.filters.metadataType" collapse-tags
          >
            <el-option
                v-for="value in metadataTypeValues"
                :key="value.value" :label="value.name" :value="value.value">
            </el-option>
          </el-select>
        </div>
        <div class="input-wrapper mt-2">
          <label>Metadata:</label>
          <el-select
              placeholder="Select values"
              v-model="chart.filters.metadata" collapse-tags
          >
            <el-option
                v-for="value in metadataValues"
                :key="value.field" :label="value.label" :value="value.field" :disabled="!chart.filters.metadataType">
            </el-option>
          </el-select>
        </div>
        <button class="search-button btn btn-block mt-3" @click="applyFilters" type="button" :disabled="!(chart.filters.metadata && chart.filters.metadataType)">
          Run
        </button>
      </div>
      <img src="../../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="download('performance')"/>
    </div>
    <div class="chart-filters-overlay" :class="{'show': chart.showFilters, hide: chart.hideOverlay}"
         @click="toggleFilters()"></div>
    <div v-if="normalizedRows.length">
      <BarChart :chart-data="parsedChartData" :options="chart.options" :data-types="chartDataTypes"
                style="height: 300px; margin-bottom: 10px"></BarChart>
      <div class="table-container">
        <div class="d-flex table-header" :style="{ minWidth: tableMinWidth }">
          <div class="column-header" :style="{ width: categoryColumnWidth }"
               @click="changeSort(categoryName)">
            <span class="mr-1" style="text-transform: capitalize">{{ parseCategory(categoryName) }}</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === categoryName && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === categoryName && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div v-for="metric in headerMetrics" class="column-header" :key="metric.name"
               :style="{ width: metricColumnWidth, textTransform: 'capitalize' }"
               @click="changeSort(metric.name)">
            <span class="mr-1" :title="parseMetric(metric.name)">{{parseMetric(metric.name)}}</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === metric.name && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === metric.name && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
        </div>
        <div style="height: calc(100% - 50px)">
          <DynamicScroller
              :items="filteredData"
              :min-item-size="50"
              class="scroller"
              :emit-update="true"
          >
            <template v-slot="{ item, index, active }">
              <DynamicScrollerItem
                  :item="item"
                  :active="active"
                  :data-index="index"
              >
                <div class="data d-flex flex-column justify-content-center align-items-center">
                  <div class="main-data" :style="{ minWidth: tableMinWidth, padding: '0 10px' }">
                    <div class="column-body" :style="{ width: categoryColumnWidth }" :title="String(item.category.value)">
                      {{ parseCategory(String(item.category.value || '-')) }}
                    </div>
                    <div v-for="metric in headerMetrics" class="column-body"
                         :key="`${metric.name}-${item.category.value}`"
                         :style="{ width: metricColumnWidth }">
                      {{ parseValue(item.metrics.find(el => el.name === metric.name)) }}
                    </div>
                  </div>
                </div>
              </DynamicScrollerItem>
            </template>
          </DynamicScroller>
        </div>
      </div>
    </div>
    <div v-else-if="localReady.performance" class="no-data d-flex justify-content-center align-items-center">
      <i v-if="searchExecuted">No data available for provided filters.</i>
      <i v-else>Select filters in order to see performance data.</i>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import BarChart from '../../shared/BarChart'
import studyGetters from '../../../store/modules/studies/getterTypes'
import { mapActions, mapGetters } from 'vuex'
import studyActions from '../../../store/modules/studies/actionTypes'
import _ from 'lodash'

export default {
  name: 'FragrancePerformanceTable',
  emits: ['update:ready'],
  components: { BarChart },
  props: {
    data: {
      type: Array,
      required: false
    },
    fragrances: {
      type: Array,
      default: null
    },
    filterFields: Object,
    ready: Object,
    searchData: Function,
    download: Function,
    parseSearchData: Function
  },
  data () {
    return {
      localReady: { ...this.ready },
      openedRows: [],
      searchExecuted: false,
      resizeToggle: false,
      sortBy: {
        key: 'client',
        desc: true
      },
      chart: {
        options: {
          scales: {
            xAxes: [{
              gridLines: {
                display: false
              },
              ticks: {
                callback: function (value) {
                  if (value.length > 18) {
                    return value.substr(0, 15) + '...'
                  } else {
                    return value
                  }
                }
              }
            }],
            yAxes: [{
              ticks: {
                min: 0
              }
            }]
          },
          tooltips: {
            enabled: true,
            mode: 'label',
            callbacks: {
              title: function (tooltipItems, data) {
                let idx = tooltipItems[0].index
                return data.labels[idx]
              }
            }
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        },
        barColors: ['#6c737e', '#7393a7', '#b5cfd8', '#e8ecf1'],
        showFilters: false,
        hideOverlay: true,
        filters: {
          metadata: '',
          metadataType: ''
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      filters: studyGetters.GET_SEARCH_FILTERS,
      rawData: studyGetters.GET_ANALYTICS_PROFILE
    }),
    normalizedRows () {
      if (!Array.isArray(this.rawData)) {
        return []
      }
      return this.rawData
        .map((row, idx) => this.normalizeRow(row, idx))
        .filter(row => row && row.category)
    },
    firstRow () {
      if (!this.normalizedRows.length) {
        return null
      }
      return this.normalizedRows[0]
    },
    categoryName () {
      return (this.firstRow && this.firstRow.category && this.firstRow.category.name) ||
        this.chart.filters.metadata ||
        'category'
    },
    headerMetrics () {
      const uniqueMetrics = new Map()
      this.normalizedRows.forEach(row => {
        const rowMetrics = Array.isArray(row.metrics) ? row.metrics : []
        rowMetrics.forEach(metric => {
          if (metric && typeof metric.name === 'string' && metric.name.trim().length && !uniqueMetrics.has(metric.name)) {
            uniqueMetrics.set(metric.name, { name: metric.name })
          }
        })
      })
      return Array.from(uniqueMetrics.values())
    },
    chartMetricNames () {
      return this.headerMetrics
        .map(metric => metric.name)
        .filter(name => this.normalizedRows.some(row => {
          const metricValue = (row.metrics || []).find(metric => metric && metric.name === name)
          return Number.isFinite(Number(metricValue && metricValue.value))
        }))
    },
    categoryColumnWidth () {
      return '260px'
    },
    metricColumnWidth () {
      return '180px'
    },
    tableMinWidth () {
      return `${260 + (this.headerMetrics.length * 180)}px`
    },
    filteredData () {
      const arrayCopy = _.cloneDeep(this.normalizedRows)
      if (!arrayCopy.length) {
        return []
      }
      const isCategorySorted = this.categoryName === this.sortBy.key
      const sortedArray = arrayCopy.sort((a, b) => {
        if (isCategorySorted) {
          const categoryA = a && a.category ? a.category.value : ''
          const categoryB = b && b.category ? b.category.value : ''
          if (categoryA < categoryB) {
            return -1
          }
          if (categoryA > categoryB) {
            return 1
          }
          return 0
        } else {
          const metricA = (a.metrics || []).find(el => el && el.name === this.sortBy.key)
          const metricB = (b.metrics || []).find(el => el && el.name === this.sortBy.key)
          const valA = Number(metricA && metricA.value)
          const valB = Number(metricB && metricB.value)
          const safeValA = Number.isFinite(valA) ? valA : 0
          const safeValB = Number.isFinite(valB) ? valB : 0
          if (safeValA < safeValB) {
            return -1
          }
          if (safeValA > safeValB) {
            return 1
          }
          return 0
        }
      })
      return this.sortBy.desc ? sortedArray : sortedArray.reverse()
    },
    metadataValues () {
      if (!(this.filterFields && this.filterFields.aggregation_metadata && this.chart.filters.metadataType)) {
        return []
      }
      const selectedCategory = this.filterFields.aggregation_metadata.find(
        el => el.category === this.chart.filters.metadataType
      )
      return selectedCategory && selectedCategory.options ? selectedCategory.options : []
    },
    metadataTypeValues () {
      return this.filterFields.aggregation_metadata ? this.filterFields.aggregation_metadata.map(el => {
        return { value: el.category, name: el.label }
      }) : []
    },
    parsedChartData () {
      if (!this.normalizedRows.length || !this.chartMetricNames.length) {
        return { labels: [], datasets: [] }
      }
      const dataClone = _.cloneDeep(this.normalizedRows)
      const primaryMetric = this.chartMetricNames[0]
      const labels = dataClone.sort((a, b) => {
        const aMetric = (a.metrics || []).find(el => el && el.name === primaryMetric)
        const bMetric = (b.metrics || []).find(el => el && el.name === primaryMetric)
        const aValue = Number(aMetric && aMetric.value)
        const bValue = Number(bMetric && bMetric.value)
        const safeAValue = Number.isFinite(aValue) ? aValue : 0
        const safeBValue = Number.isFinite(bValue) ? bValue : 0
        return safeAValue > safeBValue ? -1 : safeAValue < safeBValue ? 1 : 0
      }).map(el => (el.category && el.category.value) ? el.category.value : '')
      const data = []
      this.chartMetricNames.forEach((metric, index) => {
        const label = this.parseLabel(metric)
        const metricData = dataClone.map(el => (el.metrics || []).find(value => value && value.name === metric))
        const dataArray = []
        metricData.forEach(el => {
          const value = Number(el && el.value)
          dataArray.push(Number.isFinite(value) ? value : 0)
        })
        if (index === 0) {
          dataArray.sort((a, b) => a > b ? -1 : a < b ? 1 : 0)
        }
        data.push({
          label,
          backgroundColor: this.chart.barColors[index % this.chart.barColors.length],
          data: dataArray
        })
      })
      return {
        labels,
        datasets: data
      }
    },
    chartDataTypes () {
      const dataTypes = {}
      if (!this.chartMetricNames.length) {
        return dataTypes
      }
      this.chartMetricNames.forEach(metric => {
        const [label, aggregation] = metric.split('.')
        if (!aggregation) {
          dataTypes[this.parseLabel(label)] = 'value'
        } else {
          dataTypes[this.parseLabel(label)] = aggregation === 'mean' ? 'value' : 'percentage'
        }
      })
      return dataTypes
    }
  },
  methods: {
    ...mapActions({
      downloadXlsx: studyActions.FETCH_XLSX_FILE
    }),
    formatData () {
      return _.cloneDeep(this.normalizedRows)
    },
    normalizeRow (row, index) {
      if (!row || typeof row !== 'object') {
        return null
      }
      return {
        id: typeof row.id !== 'undefined' ? row.id : index,
        category: this.normalizeCategory(row, index),
        metrics: this.normalizeMetrics(row)
      }
    },
    normalizeCategory (row, index) {
      if (row.category && typeof row.category === 'object') {
        const name = row.category.name || this.chart.filters.metadata || 'category'
        const fallbackValue = row[name] || row.category.name || `Row ${index + 1}`
        return {
          name,
          value: typeof row.category.value === 'undefined' ? fallbackValue : row.category.value
        }
      }
      const categoryField = this.guessCategoryField(row)
      return {
        name: categoryField || 'category',
        value: Object.prototype.hasOwnProperty.call(row, categoryField) ? row[categoryField] : `Row ${index + 1}`
      }
    },
    normalizeMetrics (row) {
      const metrics = []
      const pushMetric = (name, value) => {
        if (typeof name === 'string' && name.trim().length) {
          metrics.push({ name, value })
        }
      }

      if (Array.isArray(row.metrics)) {
        row.metrics.forEach((metric, index) => {
          if (typeof metric === 'string') {
            let value = Array.isArray(row.values) ? row.values[index] : undefined
            if (typeof value === 'undefined' && Object.prototype.hasOwnProperty.call(row, metric)) {
              value = row[metric]
            }
            pushMetric(metric, value)
          } else if (metric && typeof metric === 'object') {
            const name = metric.name || metric.metric || metric.key || metric.label
            let value = metric.value
            if (typeof value === 'undefined' && typeof metric.val !== 'undefined') {
              value = metric.val
            }
            if (typeof value === 'undefined' && name && Object.prototype.hasOwnProperty.call(row, name)) {
              value = row[name]
            }
            pushMetric(name, value)
          }
        })
      } else if (row.metrics && typeof row.metrics === 'object') {
        Object.entries(row.metrics).forEach(([name, value]) => pushMetric(name, value))
      }

      if (metrics.length) {
        return metrics.filter(metric => this.isMetricField(metric.name, metric.value))
      }

      const categoryField = this.guessCategoryField(row)
      const ignoredFields = new Set([
        'id',
        'category',
        'metrics',
        'values',
        'errorlog',
        'summary',
        'grouping_present',
        'project_name',
        'product',
        'prod',
        'client',
        'c_mi_number',
        'stage'
      ])
      Object.entries(row).forEach(([field, value]) => {
        if (ignoredFields.has(field) || field === categoryField) {
          return
        }
        if (this.isMetricField(field, value)) {
          pushMetric(field, value)
        }
      })
      return metrics
    },
    guessCategoryField (row) {
      const selectedField = this.chart && this.chart.filters ? this.chart.filters.metadata : ''
      if (selectedField && Object.prototype.hasOwnProperty.call(row, selectedField)) {
        return selectedField
      }
      if (Object.prototype.hasOwnProperty.call(row, 'category') && typeof row.category !== 'object') {
        return 'category'
      }
      const priorityFields = [
        'client',
        'country',
        'region',
        'stage',
        'methodology',
        'category',
        'product',
        'project_name',
        'c_mi_number'
      ]
      const matchedField = priorityFields.find(field => Object.prototype.hasOwnProperty.call(row, field))
      if (matchedField) {
        return matchedField
      }
      const fallbackField = Object.keys(row).find(field => !this.isMetricField(field, row[field]))
      return fallbackField || 'category'
    },
    isMetricField (field, value) {
      if (typeof field !== 'string') {
        return false
      }
      if (field.includes('.')) {
        return true
      }
      const numericValue = Number(value)
      if (Number.isFinite(numericValue)) {
        return true
      }
      const aggregationNames = [
        'mean',
        'top_box',
        'bottom_box',
        'top_2_box',
        'bottom_2_box',
        'variance',
        'count',
        'percentage'
      ]
      return aggregationNames.some(name => field === name || field.endsWith(`.${name}`))
    },
    parseLabel (label) {
      if (typeof label !== 'string') {
        return ''
      }
      const labelArray = label.split('.')
      const target = labelArray[0] || ''
      return target.charAt(0).toUpperCase() + target.slice(1).replace(/_+/g, ' ')
    },
    parseMetricName (label) {
      if (typeof label !== 'string') {
        return ''
      }
      const labelArray = label.split('.')
      const metricName = labelArray.length > 1 ? labelArray[1] : labelArray[0]
      return (metricName || '').replace(/_+/g, ' ')
    },
    parseMetric (value) {
      if (typeof value !== 'string') {
        return ''
      }
      const label = this.parseLabel(value)
      const metricName = this.parseMetricName(value)
      const aggregation = value.split('.')[1]
      if (!aggregation) {
        return label
      }
      const valueType = aggregation === 'mean'
        ? '5 points'
        : ['count', 'variance'].includes(aggregation) ? 'value' : '%'
      return `${label}, ${metricName}${valueType === 'value' ? '' : ` [${valueType}]`}`
    },
    parseValue (obj) {
      if (!obj || typeof obj !== 'object') {
        return '-'
      }
      const value = Number(obj.value)
      if (!Number.isFinite(value)) {
        return obj.value === null || typeof obj.value === 'undefined' || obj.value === '' ? '-' : String(obj.value)
      }
      const aggregation = typeof obj.name === 'string' ? obj.name.split('.')[1] : ''
      if (aggregation === 'mean') {
        return value.toFixed(2)
      }
      if (['count', 'variance'].includes(aggregation)) {
        return `${Math.round(value)}`
      }
      if (aggregation) {
        return `${Math.round(value)}%`
      }
      return `${Math.round(value)}`
    },
    parseCategory (value) {
      if (typeof value !== 'string') {
        return ''
      }
      const data = value.split('.')
      return data.length > 1 ? `${data[0].replace(/_+/g, ' ')}, ${data[1].replace(/_+/g, ' ')}` : value.replace(/_+/g, ' ')
    },
    toggleFilters () {
      if (this.chart.showFilters) {
        this.chart.showFilters = false
        setTimeout(() => { this.chart.hideOverlay = true }, 200)
      } else {
        this.chart.hideOverlay = false
        setTimeout(() => { this.chart.showFilters = true }, 0)
      }
    },
    clearToggledRows () {
      this.openedRows = []
    },
    async applyFilters () {
      this.localReady.performance = false
      this.searchData(this.parseSearchData(this.filters), {
        category: this.chart.filters.metadataType,
        field: this.chart.filters.metadata
      }, 'performance')
      this.searchExecuted = true
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const test = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', test)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', true)
      }
    }
  },
  watch: {
    ready: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localReady)) {
          this.localReady = _.cloneDeep(value)
        }
      }
    },
    localReady: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.ready)) {
          this.$emit('update:ready', _.cloneDeep(value))
        }
      }
    },
    'chart.filters.metadataType': {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.chart.filters.metadata = ''
        }
      }
    },
    rawData () {
      if (this.normalizedRows.length && this.headerMetrics.length) {
        this.changeSort(this.headerMetrics[0].name)
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/variables.scss";

.table-container {
  overflow: scroll;
  position: relative;
  max-height: calc(55vh - 340px);
  min-height: 205px;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 50px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
}

.data:hover {
  background-color: #f6f6f6;
}

.scroller {
  height: 100%;
}

.column-header {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 8px;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
  font-size: 18px;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border: 1px solid #d1d4d7;
}

.no-hover:hover {
  background-color: unset;
}

.chart-filters-tab {
  overflow: hidden;
  margin-top: -1rem;
  z-index: 5;
  position: absolute;
  right: 0;
  height: 100%;
  width: 0;
  background-color: white;
  border-left: 0;
  transition: width 0.2s ease-in, border-left 0.2s ease-in;
}

.chart-filters-tab.show {
  width: 280px;
  border-left: 1px solid #d1d4d7;
}

.chart-filters-overlay {
  display: none;
  z-index: 4;
  width: 100%;
  height: 100%;
  position: absolute;
  margin-top: -1rem;
  margin-left: -1rem;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.2s ease-in;
}

.chart-filters-overlay.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.1);
}

.chart-filters-overlay.hide {
  display: none;
}

.chart-filters-button {
  cursor: pointer;
  border-left: 1px solid #d1d4d7;
  border-bottom: 1px solid #d1d4d7;
  border-top: 1px solid #d1d4d7;
  z-index: 5;
  position: absolute;
  background-color: white;
  right: 0;
  width: 40px;
  height: 40px;
  bottom: 15px;
  transition: right 0.2s ease-in;
}

.chart-filters-button.show {
  right: 280px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  color: #202124;
  margin-bottom: 2px;
  margin-left: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

.chart-filters-title {
  font-size: 1.1rem;
  white-space: nowrap;
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.download-button {
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}
</style>
